import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  type QueryKey,
  useSuspenseInfiniteQuery,
  type InfiniteData,
  type UseSuspenseInfiniteQueryResult,
} from '@github-ui/react-query'
import {reactFetch} from '@github-ui/verified-fetch'
import {parseLinkHeader} from '../../utils/parse-link-header'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {useGetThreadFromThreadID, useMarkersActions} from '../../stores/selectors'
import {fileLevelCommentKey, type LineKey} from '@github-ui/diff-lines/types'
import {OUTSIDE_DIFF_KEY} from '../../helpers/diff-helpers'

export function threadsQueryKey(pathName: string): QueryKey {
  return [PageData.threads, pathName]
}

export type ThreadDataPage = {
  data: Thread[]
  nextUrl: string | null
}

export function threadsApiUrl(pathName: string): string {
  return `${pathName}/page_data/${PageData.threads}`
}

/**
 * Hook for fetching full thread data with pagination support.
 * Returns complete thread data with all comments, unlike thread_previews which returns limited preview data.
 *
 * @param pathName - The base path for the API request
 * @param cursor - The cursor value for the next page (the minimum thread ID from the initial page)
 * @param hasMorePages - Whether there are more pages to fetch (based on whether the initial limit was exceeded)
 * @param initialThreads - Initial threads to use as the first page data (optional)
 * @returns The infinite query result with pagination controls
 */
export function useInfiniteThreadsPageData({
  pathName,
  cursor,
  hasMorePages = false,
  initialThreads = [],
}: {
  pathName: string
  cursor?: string
  hasMorePages?: boolean
  initialThreads?: Thread[]
}): UseSuspenseInfiniteQueryResult<InfiniteData<ThreadDataPage>> {
  const getThreadFromThreadID = useGetThreadFromThreadID()
  const {updateThread} = useMarkersActions()

  // Build the next page URL only if there are more pages to fetch.
  // When a cursor is provided, fetch pages after that cursor.
  // When no cursor is provided, fetch from the beginning (lazy-loading case).
  const nextPageUrl = hasMorePages
    ? cursor
      ? `${threadsApiUrl(pathName)}?after=${cursor}`
      : threadsApiUrl(pathName)
    : null

  return useSuspenseInfiniteQuery<ThreadDataPage, Error, InfiniteData<ThreadDataPage>, QueryKey, string>({
    queryKey: threadsQueryKey(pathName),
    initialPageParam: threadsApiUrl(pathName),
    initialData: {
      pages: [{data: initialThreads, nextUrl: nextPageUrl}],
      pageParams: [threadsApiUrl(pathName)],
    },
    queryFn: async ({pageParam}: {pageParam: string}): Promise<ThreadDataPage> => {
      const result = await reactFetch(pageParam)
      if (!result.ok) throw new Error(`Failed to fetch thread data from ${pageParam}: HTTP ${result.status}`)

      const json = await result.json()
      const linkHeader = result.headers.get('Link')
      const nextUrl = parseLinkHeader(linkHeader, 'next')

      const data = json as Thread[]

      // Update threads in the store with full comment data
      for (const thread of data) {
        const threadId = Number(thread.id)

        // Get existing thread by ID (will search all locations)
        const existingThread = getThreadFromThreadID(threadId)

        if (existingThread) {
          // Thread exists but doesn't have comments data yet (placeholder from diff)
          // Use its positioning to determine location
          if (existingThread.positioning) {
            const filePath = existingThread.positioning.path
            const isRightOrLeftSide = existingThread.positioning.endDiffSide === 'RIGHT' ? 'R' : 'L'
            const isFileLevelComment = thread.subjectType?.toUpperCase() === 'FILE'
            const lineKey = isFileLevelComment
              ? fileLevelCommentKey
              : existingThread.positioning.endLine
                ? (`${isRightOrLeftSide}${existingThread.positioning.endLine}` as LineKey)
                : OUTSIDE_DIFF_KEY

            // Add full data with positioning from existing thread
            updateThread(threadId, filePath, lineKey, () => {
              // Keep the comment in its loading state until it's in view
              return {
                ...thread,
                positioning: existingThread.positioning,
                hideUntilLoadedAndInView: true,
                shouldRenderInDiffLines: true,
              }
            })
          } else {
            // Thread exists but has no positioning - add with OUTSIDE_DIFF_KEY and prevent rendering in diff lines
            updateThread(threadId, OUTSIDE_DIFF_KEY, OUTSIDE_DIFF_KEY, () => {
              return {...thread, shouldRenderInDiffLines: false}
            })
          }
        } else {
          // Thread not in store and we don't have positioning info, so use OUTSIDE_DIFF_KEY as fallback
          updateThread(threadId, OUTSIDE_DIFF_KEY, OUTSIDE_DIFF_KEY, () => {
            return {...thread, shouldRenderInDiffLines: false}
          })
        }
      }

      return {data, nextUrl}
    },
    getNextPageParam: (lastPage: ThreadDataPage) => {
      return lastPage.nextUrl ?? undefined
    },
    staleTime: Infinity,
    retry: 1, // Only retry once on failure to avoid endless loops
  })
}
